import { useMutation, UseMutationResult, useQueryClient } from 'react-query';

import { getTokenWithType } from './useAuth';

import { getAccountInfo as updateAccountInfo, saveAccounts } from 'utils/urls';

import { AccountInputs, AccountFormInputs } from 'types/accounts';
import { ErrorModal } from 'types/modalTypes';

export async function postAccountInfo(
  params: AccountFormInputs,
): Promise<TResult> {
  const { Id } = params;
  const response = await fetch(
    params.Id === 0 || params.Id === null
      ? saveAccounts
      : updateAccountInfo(Id),
    {
      method: Id === 0 ? 'POST' : 'PUT',
      headers: {
        Authorization: getTokenWithType(),
        'content-type': 'application/json',
      },
      body: JSON.stringify(params),
    },
  );
  return response.json();
}

type TError = ErrorModal;

export type TResult = {
  Data: AccountInputs;
  IsSuccess: boolean;
  StatusCode: number;
} & ErrorModal &
  Array<string>;

type TVariables = AccountFormInputs;

type TSnapshot = unknown;

async function wait(millis: number) {
  return new Promise(resolve => {
    setTimeout(resolve, millis);
  });
}

function useSaveAccountInfo(): UseMutationResult<
  TResult,
  TError,
  TVariables,
  TSnapshot
> {
  const queryClient = useQueryClient();
  return useMutation(async (params: AccountFormInputs) => {
    const newAccountInfo = await postAccountInfo(params);

    if (newAccountInfo.StatusCode === 200) {
      if (params.Id !== 0) {
        queryClient.invalidateQueries(['get_account', params.Id]);
        await wait(2000);
      }
    } else throw newAccountInfo;

    return newAccountInfo;
  });
}

export { useSaveAccountInfo };
